import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Typography,
  IconButton,
  Button,
  Card,
  CardContent,
  Chip,
  Autocomplete,
  TextField,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  SwapHoriz as SwapHorizIcon,
  PersonAdd as PersonAddIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { sgpbusinessAPI, gebizAPI } from '../../services/api';

/**
 * Reviews the SGPBusiness fuzzy-match queue. Each item can be confirmed,
 * reassigned to a different lead, turned into a new lead, or skipped. Consumes
 * GET /sgpbusiness/review and PATCH /sgpbusiness/{company_id}/review.
 */
const SGPBusinessReviewDialog = ({ open, onClose, jobId }) => {
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);
  const [reassignFor, setReassignFor] = useState(null); // company_id being reassigned
  const [selectedLead, setSelectedLead] = useState(null); // { id, company_name }
  const [searchQuery, setSearchQuery] = useState('');

  const { data: queue, isLoading } = useQuery(
    ['sgpbusinessReview', jobId],
    () => sgpbusinessAPI.getReviewQueue(jobId),
    { enabled: open && !!jobId }
  );

  const { data: searchResults, isLoading: isSearching } = useQuery(
    ['leadSearch', searchQuery],
    () => gebizAPI.searchLeads(searchQuery, 20),
    { enabled: searchQuery.length >= 2, keepPreviousData: true }
  );

  const reviewMutation = useMutation(
    ({ companyId, payload }) => sgpbusinessAPI.reviewCompany(jobId, companyId, payload),
    {
      onSuccess: () => {
        setError(null);
        setReassignFor(null);
        setSelectedLead(null);
        setSearchQuery('');
        queryClient.invalidateQueries(['sgpbusinessReview', jobId]);
        queryClient.invalidateQueries(['sgpbusinessStatus', jobId]);
        queryClient.invalidateQueries(['jobStats', jobId]);
        queryClient.invalidateQueries(['jobLeads', jobId]);
        queryClient.invalidateQueries(['jobLeadsCount', jobId]);
      },
      onError: (err) =>
        setError(err.response?.data?.detail || err.message || 'Review failed'),
    }
  );

  const act = (companyId, action, extra = {}) =>
    reviewMutation.mutate({ companyId, payload: { action, ...extra } });

  const busy = reviewMutation.isLoading;
  const items = queue || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Review Fuzzy Matches</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            No fuzzy matches awaiting review.
          </Typography>
        ) : (
          <Stack spacing={2}>
            {items.map((item) => (
              <Card key={item.company_id} variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {item.company_name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, mb: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Best match: {item.matched_company_name || '(none)'}
                    </Typography>
                    {item.match_confidence != null && (
                      <Chip size="small" label={`${item.match_confidence}%`} />
                    )}
                  </Box>

                  {reassignFor === item.company_id ? (
                    <Box>
                      <Autocomplete
                        options={searchResults || []}
                        getOptionLabel={(o) => `${o.company_name} (ID: ${o.id})`}
                        loading={isSearching}
                        onInputChange={(_, v) => setSearchQuery(v)}
                        onChange={(_, v) => setSelectedLead(v)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label="Search lead"
                            placeholder="Type company name..."
                          />
                        )}
                      />
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={!selectedLead || busy}
                          onClick={() => act(item.company_id, 'assign_existing', { lead_id: selectedLead.id })}
                        >
                          Assign
                        </Button>
                        <Button
                          size="small"
                          onClick={() => { setReassignFor(null); setSelectedLead(null); }}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircleIcon />}
                        disabled={busy}
                        onClick={() => act(item.company_id, 'confirm')}
                      >
                        Confirm
                      </Button>
                      <Button
                        size="small"
                        startIcon={<SwapHorizIcon />}
                        disabled={busy}
                        onClick={() => { setReassignFor(item.company_id); setSelectedLead(null); setSearchQuery(''); }}
                      >
                        Reassign
                      </Button>
                      <Button
                        size="small"
                        startIcon={<PersonAddIcon />}
                        disabled={busy}
                        onClick={() => act(item.company_id, 'create_new')}
                      >
                        New Lead
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<BlockIcon />}
                        disabled={busy}
                        onClick={() => act(item.company_id, 'skip')}
                      >
                        Skip
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SGPBusinessReviewDialog;
